import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useQueryClient } from 'react-query';
import omit from 'lodash/omit';

import AgGrid from '../../shared/agGrid/agGrid.component';
import Button from '../../shared/button.component';
import useGridApi from '../../shared/hooks/useGridApi.hook';
import {
  ScenarioPropType,
  ReportFieldDefinitionPropType,
} from '../shared/propTypes';

import { YES_FLAG } from '../../shared/constants';
import { gridOptions, LABEL_COLUMN_ID } from './constants';
import ScenarioNavbar from '../shared/scenarioNavbar.component';

import {
  isLoadingUpdateReportValueSelector,
  isLoadingResettingReportValueSelector,
} from './store/selectors';

import {
  updateReportValue,
  resetReportValue,
} from './store/actions';

import {
  fetchScenarioReportFields,
} from '../shared/store/actions';

import {
  selectedScenarioCurrencySelector,
  selectedScenarioToCompareSelector,
  isLoadingCalculatingScenarioSelector,
  reportsNamesSelector,
  isLoadingReportFieldsSelector,
  reportFieldsDefinitionSelector,
  isUploadingTaggedDataSelector,
  scenariosSelector,
  selectedScenarioViewSelector,
  currenciesOptionsSelector,
} from '../shared/store/selectors';

import {
  environmentSuffixSelector,
} from '../../shared/store/selectors';
import {
  hideDecimalsSelector,
  negativeMinusSelector,
  displayZeroSelector,
} from '../../userData/store/selectors';

import { getColumnDefinitions } from './columnDefinitions';

import { getUniqCurrencies } from './utils';
import { useCanUserEditScenario, useCurrentClientId } from '../shared/utils';
import { fromSearchString } from '../../shared/urlUtils';

import { QueryKeys } from '@queries/index';
import { useCombinedScenarioReportsData, useScenarioDetailsForExport, useScenarioMembers } from '@queries/scenarios';

import downloadScenarioReportXlsx from './downloadScenarioReportXlsx';
import { OptionsPropType } from '../../shared/propTypes/common';
import MultipleCurrenciesWarning from '../shared/multipleCurrenciesWarning.component';
import { retrieveDataFromTotalColumn } from './totalColumn';
import { functionalCurrencyOption } from '../shared/constants';
import { useRestoreScenarioViewOptions } from '../shared/useRestoreScenarioViewOptions.hook';
import { useRestoreColumnsState } from '../shared/useRestoreColumnsState.hook';
import { masterGridOptions } from './RowDetails/masterGridOptions';
import { useColumnResizeOnGroupToggle } from './useColumnResizeOnGroupToggle';

/**
 * @typedef { import("ag-grid-community/dist/lib/events").CellDoubleClickedEvent } CellDoubleClickedEvent
 */

const ScenarioReportWithDetails = ({
  location: {
    search,
  },
  match: {
    params: {
      scenarioId,
    },
  },
  allScenarios,

  selectedScenarioToCompare,
  selectedCurrency,

  reportsNames,

  fetchScenarioReportFields,
  isLoadingReportFields,

  updateReportValue,
  isLoadingUpdatingReportValue,

  isLoadingResettingReportValue,

  isLoadingCalculating,
  isUploadingTaggedData,

  reportFields,

  currenciesOptions,

  hideDecimals,
  negativeMinus,
  shouldDisplayZero,
  environmentSuffix,
}) => {
  const clientId = useCurrentClientId();
  useRestoreScenarioViewOptions(scenarioId);
  const { gridApi, onGridReady } = useGridApi();

  const {
    onRowGroupOpened,
  } = useColumnResizeOnGroupToggle();

  const {
    resetColumnState,
    onColumnResized,
    ...restoreDisplayedColumnsGridProps
  } = useRestoreColumnsState(
    `scenario-reports-grid-columns-state-${scenarioId}@client-${clientId}`,
    gridApi,
    {
      preprocessBeforeLoad: state => state.map(colState => {
        if (colState.colId === LABEL_COLUMN_ID) {
          return omit(colState, ['width', 'pinned']);
        }
        return colState;
      }),
    }
  );

  const {
    companyCodes,
    countryTaxTypes,
    groupField,
  } = useMemo(() => fromSearchString(search, true), [search]);

  const queryClient = useQueryClient();

  const refetchReportsData = useCallback(() => queryClient.resetQueries({
    predicate: query => [
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
      QueryKeys.scenarios.ScenarioDetailsList,
      QueryKeys.scenarios.ScenarioDetailsForExport,
      QueryKeys.scenarios.ScenarioRowDetailsList,
    ].includes(query.queryKey[0]),
  }), [queryClient]);

  const scenarioSettingsCollection = useMemo(
    () => ({
      base: allScenarios.find(scenario => `${scenario.scenarioId}` === `${scenarioId}`),
      compare: allScenarios.find(scenario => `${scenario.scenarioId}` === `${selectedScenarioToCompare}`),
    }),
    [allScenarios, scenarioId, selectedScenarioToCompare]
  );

  const canModifyBase = useCanUserEditScenario(scenarioId);
  const canModifyCompare = useCanUserEditScenario(selectedScenarioToCompare);

  useEffect(() => {
    fetchScenarioReportFields();
  }, [fetchScenarioReportFields]);

  const { data: scenarioMembers, isLoading: isLoadingScenarioMembers } = useScenarioMembers({ scenarioId });
  const { data: compareScenarioMembers, isLoading: isLoadingCompareScenarioMembers } = useScenarioMembers({
    scenarioId: selectedScenarioToCompare,
  });

  const scenarioMembersMap = useMemo(
    () => scenarioMembers.reduce((acc, { companyCode }) => {
      acc[companyCode] = companyCode;
      return acc;
    }, {}),
    [scenarioMembers]
  );
  const compareScenarioMembersMap = useMemo(
    () => compareScenarioMembers.reduce((acc, { companyCode }) => {
      acc[companyCode] = companyCode;
      return acc;
    }, {}),
    [compareScenarioMembers]
  );

  const [reportsData, isFetchingReportsData] = useCombinedScenarioReportsData({
    scenarioId,
    companyCodes,
    countryTaxTypes,
    compareScenarioId: selectedScenarioToCompare,
    reportingCurrency: selectedCurrency,
    reportsNames,
  });

  const hasDifferentCurrencies = useMemo(() => getUniqCurrencies(reportsData).length > 1,
    [reportsData]);

  const isLoading = isLoadingReportFields
    || isLoadingScenarioMembers
    || isLoadingCompareScenarioMembers
    || isFetchingReportsData
    || isLoadingCalculating
    || isUploadingTaggedData
    || isLoadingUpdatingReportValue
    || isLoadingResettingReportValue;

  const columnDefinitions = useMemo(() => isLoading
    ? []
    : getColumnDefinitions({
      reportsData,
      isComparingMode: Boolean(selectedScenarioToCompare),
      isFunctionalCurrencySelected: selectedCurrency === functionalCurrencyOption.value,
      scenarioSettingsCollection,
      setValue: ({ reportName, fieldName, countryTaxType, value, compareScenario, companyCode }) => {
        // do not update report on null/undefined/NaN value
        if (value || value === 0) {
          updateReportValue({
            scenarioId: compareScenario ? selectedScenarioToCompare : scenarioId,
            companyCode,
            countryTaxType,
            fieldName,
            value,
            reportName,
          }, { onSuccessCallback: refetchReportsData });
        }
      },
      scenarioMembersMap,
      compareScenarioMembersMap,
    }), [
    reportsData,
    scenarioSettingsCollection,
    selectedScenarioToCompare,
    updateReportValue,
    scenarioId,
    refetchReportsData,
    isLoading,
    selectedCurrency,
    scenarioMembersMap,
    compareScenarioMembersMap,
  ]);


  const getContextMenuItems = useCallback(({ api, node, column }) => {
    const { isBaseScenario, baseScenarioSettings, compareScenarioSettings } = column.colDef;
    if (!baseScenarioSettings) {
      return null;
    }
    const clickedScenarioSettings = isBaseScenario
      ? baseScenarioSettings.lockedFlag
      : compareScenarioSettings.lockedFlag;
    const fieldEditable = node.data.editable && node.data.editable[column.userProvidedColDef.reportName];

    const baseLocked = baseScenarioSettings.lockedFlag === YES_FLAG;
    const compareLocked = compareScenarioSettings?.lockedFlag === YES_FLAG;

    const isFunctionalCurrencySelected = selectedCurrency === functionalCurrencyOption.value;
    const canBaseBeModified = !baseLocked && canModifyBase && isFunctionalCurrencySelected;
    const canCompareBeModified = !compareLocked && canModifyCompare && isFunctionalCurrencySelected;
    const canClickedBeModified = isBaseScenario ? canBaseBeModified : canCompareBeModified;

    if (!clickedScenarioSettings) {
      return null;
    }
    if (!column.userProvidedColDef.reportName || !fieldEditable) {
      return null;
    }

    const overrideValueItem = {
      name: 'Overwrite value',
      action: () => api.startEditingCell({
        rowIndex: node.rowIndex,
        colKey: column.colId,
      }),
    };

    if (!canBaseBeModified && !canCompareBeModified) {
      return null;
    }
    if (canClickedBeModified) {
      return [overrideValueItem];
    }
    return null;
  }, [canModifyBase, canModifyCompare, selectedCurrency]);

  const handleCellDoubleClick = useCallback(
    /** @param { CellDoubleClickedEvent } doubleClickEvent */
    doubleClickEvent => {
      const { node, column, api } = doubleClickEvent;
      if (column.isCellEditable(node)) {
        api.startEditingCell({
          rowIndex: node.rowIndex,
          colKey: column.colId,
        });
      }
    }, []);

  const displayedReportFields = useMemo(
    () => reportFields.filter(({ hidden }) => !hidden),
    [reportFields]
  );

  const displayedScenarioParams = useMemo(() => {
    const baseScenario = scenarioSettingsCollection.base;
    const compareScenario = scenarioSettingsCollection.compare;
    const currency = currenciesOptions.find(({ value }) => value === selectedCurrency);

    return [
      { label: 'View', value: 'Tax Provision' },
      { label: 'Currency', value: currency ? currency.label : '' },
      { label: 'Company Codes', value: companyCodes?.join(',') },
      { label: 'Scenario', value: baseScenario ? baseScenario.scenarioName : '' },
      { label: 'Scenario Period', value: baseScenario ? baseScenario.periodName : '' },
      ...(compareScenario ? [
        { label: 'Compare Scenario', value: compareScenario ? compareScenario.scenarioName : '' },
        { label: 'Compare Scenario Period', value: compareScenario ? compareScenario.periodName : '' },
      ] : []),
    ];
  }, [
    companyCodes,
    scenarioSettingsCollection,
    selectedCurrency,
    currenciesOptions,
  ]);
  const reportParams = useMemo(() => Object.entries(reportsData).map(([reportName, rows]) => ({
    reportName,
    countryTaxTypes: rows.map(({ COUNTRY_TAX_TYPE }) => COUNTRY_TAX_TYPE),
  })), [reportsData]);


  const { refetch: fetchDetails } = useScenarioDetailsForExport({
    reportParams,
    companyCodes,
    reportingCurrency: selectedCurrency,
    scenarioId,
    compareScenarioId: selectedScenarioToCompare,
  });

  const exportToExcel = useCallback(done => {
    const isComparing = Boolean(selectedScenarioToCompare);

    const totalColumn = retrieveDataFromTotalColumn(gridApi, isComparing);

    downloadScenarioReportXlsx({
      displayedReportFields,
      fetchDetails,

      isComparing,

      reportsData,
      displayedScenarioParams,
      hideDecimals,
      negativeMinus,
      shouldDisplayZero,
      environmentSuffix,
      groupField,
      totalColumn,
    }).finally(done);
  }, [
    displayedReportFields,
    fetchDetails,
    selectedScenarioToCompare,
    reportsData,
    displayedScenarioParams,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    environmentSuffix,
    groupField,
    gridApi,
  ]);

  const collapseAll = useCallback(() => {
    gridApi.forEachNode(node => {
      if (node.expanded) {
        node.setExpanded(false);
      }
    });
  }, [gridApi]);

  const resetView = useCallback(() => {
    resetColumnState();
    collapseAll();
  }, [resetColumnState, collapseAll]);

  return (
    <div className="scenario-report-page">
      <div className="page">
        <div className="container-fluid">
          <ScenarioNavbar
            onRefresh={refetchReportsData}
            showBackToMemberListButton
            showAddMembersButton={false}
            showMembersDropdown
            exportToExcel={exportToExcel}
            disableExporting={isLoading}
          />
          {
            companyCodes
              ? (
                <>
                  <div className="ag-grid-full-page-with-secondary-menu">
                    <MultipleCurrenciesWarning hasDifferentCurrencies={hasDifferentCurrencies} />
                    <div className="d-flex justify-content-end mb-2">
                      <Button kind="secondary" onClick={collapseAll}>
                        Collapse all
                      </Button>
                      <Button kind="secondary" className="ml-2" onClick={resetView}>
                        Reset grid view
                      </Button>
                    </div>

                    <AgGrid
                      columnDefs={columnDefinitions}
                      rowData={displayedReportFields}
                      isGridLoading={isLoading}
                      createCustomContextMenu={getContextMenuItems}
                      onCellDoubleClicked={handleCellDoubleClick}
                      suppressClickEdit
                      suppressSizeColumnsToFit
                      stopEditingWhenCellsLoseFocus
                      headerHeight={30}
                      onGridReady={onGridReady}
                      onRowGroupOpened={onRowGroupOpened}
                      onColumnResized={onColumnResized}
                      {...restoreDisplayedColumnsGridProps}
                      {...gridOptions}
                      {...masterGridOptions}
                    />
                  </div>
                </>
              ) : null
          }
        </div>
      </div>
    </div>
  );
};


ScenarioReportWithDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      scenarioId: PropTypes.string.isRequired,
    }),
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  selectedScenarioToCompare: PropTypes.number,
  selectedCurrency: PropTypes.string,

  reportsNames: PropTypes.arrayOf(PropTypes.string),

  fetchScenarioReportFields: PropTypes.func.isRequired,
  isLoadingReportFields: PropTypes.bool,

  reportFields: PropTypes.arrayOf(ReportFieldDefinitionPropType),

  updateReportValue: PropTypes.func.isRequired,
  isLoadingUpdatingReportValue: PropTypes.bool.isRequired,

  resetReportValue: PropTypes.func.isRequired,
  isLoadingResettingReportValue: PropTypes.bool.isRequired,

  isLoadingCalculating: PropTypes.bool.isRequired,
  isUploadingTaggedData: PropTypes.bool.isRequired,

  allScenarios: PropTypes.arrayOf(ScenarioPropType),

  environmentSuffix: PropTypes.string.isRequired,
  shouldDisplayZero: PropTypes.bool.isRequired,
  hideDecimals: PropTypes.bool.isRequired,
  negativeMinus: PropTypes.bool.isRequired,

  currenciesOptions: OptionsPropType,
};

const mapStateToProps = state => ({
  selectedScenarioToCompare: selectedScenarioToCompareSelector(state),
  selectedCurrency: selectedScenarioCurrencySelector(state),
  selectedView: selectedScenarioViewSelector(state),

  currenciesOptions: currenciesOptionsSelector(state),

  reportsNames: reportsNamesSelector(state),

  reportFields: reportFieldsDefinitionSelector(state),

  isLoadingReportFields: isLoadingReportFieldsSelector(state),

  isLoadingUpdatingReportValue: isLoadingUpdateReportValueSelector(state),
  isLoadingResettingReportValue: isLoadingResettingReportValueSelector(state),

  isLoadingCalculating: isLoadingCalculatingScenarioSelector(state),
  isUploadingTaggedData: isUploadingTaggedDataSelector(state),

  allScenarios: scenariosSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(
  mapStateToProps,
  {
    fetchScenarioReportFields,
    updateReportValue,
    resetReportValue,
  }
)(withRouter(ScenarioReportWithDetails));
